import {
  InfoConsent,
  InfoValueConsent,
  RightCardsColumn,
  RightCardColumn,
  MiddleCardsColumn,
  CardsContainer,
  LeftCardsColumn,
  LeftLowerCardsRow,
  StyledSection,
} from './CampaignPane.styles';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { useTranslation } from 'react-i18next';
import { getCopiesCampaignDetails as getCopies, getCampaignMediumItems } from '../Campaign.helper';
import { Campaign } from '@/modules/study-insights/services/campaign/campaign.type';
import { formatDate } from '@common/utils';
import { formatNumber, numberToPercentage } from '@modules/study-insights/utils';
import { CampaignCard } from './components/CampaingCard';
import { MetricCard } from './components/MetricCard';

interface Props {
  campaign: Campaign;
  onEdit?: () => void;
  onCopy?: () => void;
}

const CampaignPane = ({ campaign, onCopy, onEdit }: Props): JSX.Element => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const copies = getCopies({ t });
  const campaignMediumItems = getCampaignMediumItems(t);

  const campaignMedium =
    campaignMediumItems.find((item) => item.id === campaign.campaignMedium)?.label ?? campaign.campaignMedium;

  const isVolumeNotDefined =
    (campaign.consentInfo?.conversionRate === 0 || campaign?.consentInfo?.conversionRate === undefined) &&
    campaign.volumeDistribution === null;

  return (
    <StyledSection data-testid={`campaign-item`}>
      <CampaignCard header={{ title: campaign.campaignName }} onCopy={onCopy} onEdit={onEdit}>
        <CardsContainer>
          <LeftCardsColumn>
            <MetricCard
              id={`campaign-consents-value`}
              title={copies.campaignConsents.label}
              value={<InfoConsent>{formatNumber(campaign.consentInfo?.campaignConsents ?? 0)}</InfoConsent>}
              boldTitle
            />
            <LeftLowerCardsRow>
              <MetricCard
                id={`campaign-conversion-rate-value`}
                title={copies.campaignConversionRate.label}
                secondaryLabel={copies.campaignConversionRate.secondaryLabel}
                value={
                  isVolumeNotDefined ? (
                    t('campaign_list.no_volume_defined')
                  ) : (
                    <InfoValueConsent>{numberToPercentage(campaign.consentInfo?.conversionRate ?? 0)}</InfoValueConsent>
                  )
                }
                boldTitle
              />
              <MetricCard
                id={`campaign-proportion-value`}
                title={copies.campaignProportion.label}
                secondaryLabel={copies.campaignProportion.secondaryLabel}
                value={<InfoValueConsent>{numberToPercentage(campaign.consentInfo?.proportion ?? 0)}</InfoValueConsent>}
                boldTitle
              />
            </LeftLowerCardsRow>
          </LeftCardsColumn>
          <MiddleCardsColumn>
            <MetricCard
              id={`campaign-launch-date-value`}
              title={copies.campaignLaunchDate.label}
              value={campaign.launchDate ? formatDate(campaign.launchDate) : '-'}
            />
            <MetricCard
              id={`campaign-end-date-value`}
              title={copies.campaignEndDate.label}
              value={campaign.endDate ? formatDate(campaign.endDate) : '-'}
            />
          </MiddleCardsColumn>
          <RightCardsColumn>
            <RightCardColumn>
              <MetricCard
                id={`campaign-medium-value`}
                title={copies.campaignMedium.label}
                value={campaignMedium ?? ''}
              />
              <MetricCard
                id={`campaign-term-value`}
                title={copies.campaignTermInfo.label}
                value={campaign.campaignTerm ?? ''}
              />
            </RightCardColumn>
            <RightCardColumn>
              <MetricCard
                id={`campaign-source-value`}
                title={copies.campaignSourceInfo.label}
                value={campaign.campaignSource ?? ''}
              />
              <MetricCard
                id={`campaign-content-value`}
                title={copies.campaignContent.label}
                value={campaign.campaignContent ?? ''}
              />
            </RightCardColumn>
          </RightCardsColumn>
        </CardsContainer>
      </CampaignCard>
    </StyledSection>
  );
};

export default CampaignPane;
